import React, { useCallback, useContext } from 'react';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';

import { InventoryTabItemName } from '../../../../../InventoryStore';
import { useRowConfig } from '../../../../../hooks';
import { useInventoryPermissions } from '../../../../../hooks/useInventoryPermissions';
import { getPricingRuleBadge } from '../../../../../utils/badges';
import type { RoomOpeningInventory } from '../../../../../utils/rooms';
import { RowBase } from '../../../../Table/RowBase/RowBase';
import type { InventoryValue } from '../../../inventoryStatusUtils';

const RoomDiscountRowComponent: React.FC<{ roomId: number }> = ({ roomId }) => {
  const { inventoryRooms, formatter, onDuplicate } = useRowConfig({
    entityId: roomId,
    entityType: InventoryTabItemName.Rooms,
    fieldName: 'discount',
    formatterName: 'discount',
  });
  const { canEditDiscountPrice } = useInventoryPermissions();
  const { hotelInfo } = useContext(HotelContext);
  const { roomsList } = useContext(ModernHotelContext);
  const room = roomsList.find((r) => r.id === roomId);

  const handleGetBadge = useCallback(
    (values: InventoryValue, date: string) => {
      const roomValues = values as RoomOpeningInventory;

      return getPricingRuleBadge({
        stockScrapperEnabled: hotelInfo?.stockScrapperEnabled ?? false,
        lastMinuteDiscount: hotelInfo?.lastMinuteNightRoomDiscountRate,
        lastMinuteDays: hotelInfo?.lastMinuteNightRoomDiscountDays,
        bookingcomId: room?.bookingcomId,
        discountMode: roomValues.discountMode,
        discountPrice: roomValues.discountPrice,
        date,
      });
    },
    [
      hotelInfo?.stockScrapperEnabled,
      hotelInfo?.lastMinuteNightRoomDiscountRate,
      hotelInfo?.lastMinuteNightRoomDiscountDays,
      room?.bookingcomId,
    ]
  );

  if (!inventoryRooms) {
    return null;
  }

  return (
    <RowBase
      label="Room discount"
      entityId={roomId}
      fieldName="discount"
      entityType={InventoryTabItemName.Rooms}
      formatter={formatter}
      onDuplicate={onDuplicate}
      isEditable={canEditDiscountPrice}
      getBadge={handleGetBadge}
    />
  );
};

export const RoomDiscountRow = React.memo(RoomDiscountRowComponent);
